// Bloque `{tipo:'visual', contenido, visual}` del chat → @orion/visuals (carga perezosa).
// Si el paquete tarda o falla, se ve el `contenido` (texto de respaldo) — nunca un hueco.
import { Component, lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import type { OrionVisualProps } from '@orion/visuals';

const LazyVisualBlock = lazy(() => import('./orionVisuals').then((m) => ({ default: m.VisualBlock })));

class LoadBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export interface VisualBloqueProps {
  bloque: { tipo: string; contenido?: unknown; visual?: unknown };
  /** Cómo pinta el chat el texto de respaldo (su Markdown). */
  renderFallback: (text: string) => ReactNode;
}

export function VisualBloque({ bloque, renderFallback }: VisualBloqueProps) {
  const text = typeof bloque.contenido === 'string' ? bloque.contenido : '';
  const fallback = <>{text ? renderFallback(text) : null}</>;
  return (
    <LoadBoundary fallback={fallback}>
      <Suspense fallback={<div className="h-48 w-full animate-pulse rounded-2xl bg-muted" aria-label="Cargando visualización" />}>
        <LazyVisualBlock bloque={bloque} renderFallback={renderFallback} />
      </Suspense>
    </LoadBoundary>
  );
}

/**
 * Para pantallas del dashboard: `const Chart = lazyVisual('VisualChart')` y luego
 * `<Chart title="…" data={{ type: 'line', x: 'mes', rows, series }} />`.
 */
type ScreenName = 'OrionVisual' | 'VisualTable' | 'VisualChart' | 'VisualKpi' | 'VisualOrgChart' | 'VisualDiagram' | 'VisualTimeline' | 'VisualFunnel' | 'VisualProgress' | 'VisualList';
export function lazyVisual(name: ScreenName) {
  const L = lazy(() => import('./orionVisuals').then((m) => ({ default: m[name] as ComponentType<any> })));
  return function LazyScreenVisual(props: Record<string, unknown> & Partial<Pick<OrionVisualProps, 'variant'>>) {
    return (
      <Suspense fallback={<div className="h-64 w-full animate-pulse rounded-2xl bg-muted" />}>
        <L {...props} />
      </Suspense>
    );
  };
}
